/**
 * "Move out a resident" — the EHR-managed variant.
 *
 * Replaces moveOutResident on campuses where the EHR owns resident data. The
 * discharge is recorded in the partner system and arrives here as a task to
 * approve on /tasks/ehr.
 *
 * Same title as the portal flow; the gates are exactly complementary so only one
 * of the two is ever offered. `placement` is omitted (vestigial — see
 * SpotlightOverlay.jsx).
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §5.2 for the anchors and §10 Decision 2.
 */

export default {
    id: 'move-out-resident-ehr',
    title: 'Move out a resident',
    description: 'Approve the move-out task sent by your EHR.',

    minRoleWeight: 11,

    /* Exact inverse of moveOutResident. */
    available: (ctx) => ctx.ehrActive,

    startsAt: 'This will take you to the EHR Tasks page.',

    steps: [
        {
            id: 'open-subtab',
            route: { path: '/tasks/ehr', navigate: true },
            anchor: 'ehr.subtab-remove',
            title: 'Open the Move-out Resident tasks',
            body: 'On the Residents tab, choose Move-out Resident. The number on the tab is how many tasks are waiting. If you are looking at the Users tab, switch to Residents first.',
            /* Keyed on `.is-active` rather than [aria-selected="true"] — the
               engine observes class/style/disabled, so the class flip is caught
               instantly (detectors.js). */
            done: { type: 'dom', selector: '[data-help-id="ehr.subtab-remove"].is-active' },
        },
        {
            id: 'open-task',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.task-row',
            title: 'Open a move-out task',
            body: 'Click Review on the row for the resident who has left. If the list is empty there is nothing waiting for approval right now.',
            done: { type: 'dom', selector: '[data-help-id="ehr.approve-remove"]' },
        },
        {
            id: 'approve',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.approve-remove',
            title: 'Check the resident, then approve',
            body: 'Confirm the dialog names the right resident, then click Approve. Once approved they are no longer active, and their pendant devices stop being monitored and will not generate alerts.',
            /* Presence, not :not([disabled]) — Approve re-disables while the write
               is in flight. */
            context: {
                selector: '[data-help-id="ehr.approve-remove"]',
                contextStep: 'open-task',
            },
            done: { type: 'transaction', transTypes: ['resident_remove_task'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Move-out approved',
        body: 'The resident is no longer active. Their devices are free to reassign to someone else.',
    },
};
